import { InfoOutlined, VisibilityOutlined } from "@mui/icons-material";
import { Box, Card, CardContent, Typography, Chip, IconButton, Tooltip, LinearProgress } from "@mui/material";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';
import LoadingIndicator from './LoadingIndicator';
import PlanoAcaoResultadoConsulta from "../types/negocio/PlanoAcaoResultadoConsulta";
import PlanoDeAcaoDetalhesModal from "./modais/PlanoDeAcaoDetalhesModal";
import { calcularPercentualConclusao, formatarPercentual, getCorProgressBar } from '../utils/planoAcaoUtils';

interface CardsPlanoDeAcaoProps {
    readonly planos: PlanoAcaoResultadoConsulta[];
    readonly loading: boolean;
    readonly onArquivar: (guid: string) => void;
    readonly onEditar: (guid: string) => void;
}

export function CardsPlanoDeAcao({ planos, loading, onArquivar, onEditar }: CardsPlanoDeAcaoProps) {
    const [planoSelecionado, setPlanoSelecionado] = useState<PlanoAcaoResultadoConsulta | null>(null);
    const [modalDetalhesAberto, setModalDetalhesAberto] = useState(false);

    const handleVerDetalhes = (plano: PlanoAcaoResultadoConsulta) => {
        setPlanoSelecionado(plano);
        setModalDetalhesAberto(true);
    };

    const handleFecharModal = () => {
        setModalDetalhesAberto(false);
        setPlanoSelecionado(null);
    };

    const getStatusColor = (status: string) => {
        const statusLower = status?.toLowerCase() || '';
        if (statusLower.includes('finalizada')) return 'success';
        if (statusLower.includes('em andamento')) return 'info';
        if (statusLower.includes('pendente') || statusLower.includes('cancelada')) return 'error';
        if (statusLower.includes('planejada')) return 'default';
        return 'warning';
    };

    const getPrioridadeColor = (prioridade: string): "error" | "warning" | "info" | "success" | "default" => {
        switch (prioridade) {
            case 'Muito alta': return 'error';
            case 'Alta': return 'warning';
            case 'Média': return 'info';
            case 'Baixa': return 'success';
            case 'Muito baixa': return 'default';
            default: return 'default';
        }
    };

    if (loading) {
        return <LoadingIndicator message="Carregando planos de ação..." />;
    }

    if (planos.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1">Nenhum plano de ação encontrado.</Typography>
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: 3,
                    width: '100%',
                }}
            >
                {planos.map((plano, index) => {
                    const prazoFormatado = plano.prazoConclusao 
                        ? format(new Date(plano.prazoConclusao), 'dd/MM/yyyy', { locale: ptBR }) 
                        : '-';
                    
                    // Cálculo do percentual de conclusão
                    const percentualConclusao = calcularPercentualConclusao(plano);
                    const corProgressBar = getCorProgressBar(percentualConclusao);
                    
                    return (
                        <Card 
                            key={plano.guid ?? index}
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 6,
                                },
                                border: '1px solid',
                                borderColor: 'divider',
                                position: 'relative',
                                overflow: 'hidden',
                                borderRadius: '8px',
                            }}
                            onClick={() => handleVerDetalhes(plano)}
                        >
                            {/* Barra de cor da estratégia */}
                            <Box 
                                sx={{ 
                                    height: '8px', 
                                    bgcolor: 'primary.main',
                                    width: '100%',
                                    borderTopLeftRadius: '8px',
                                    borderTopRightRadius: '8px'
                                }} 
                            />
                            
                            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5, p: 2.5 }}>
                                {/* Header com estratégia */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                    <Chip 
                                        label={plano.estrategia || 'Sem Estratégia'} 
                                        size="small"
                                        color={plano.estrategia ? "primary" : "default"}
                                        sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: '0.7rem',
                                            height: '24px'
                                        }}
                                    />
                                    <Tooltip title="Ver detalhes">
                                        <IconButton 
                                            size="small" 
                                            sx={{ 
                                                bgcolor: 'action.hover',
                                                '&:hover': { bgcolor: 'primary.main', color: 'white' }
                                            }}
                                        >
                                            <VisibilityOutlined fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                {/* Ação principal */}
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography 
                                        variant="body1" 
                                        sx={{ 
                                            fontWeight: 'bold',
                                            lineHeight: 1.4,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            minHeight: '60px'
                                        }}
                                    >
                                        {plano.acao}
                                    </Typography>
                                </Box>

                                {/* Informações resumidas */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 'auto' }}>
                                    {/* Progress Bar - Estilo Jira */}
                                    <Tooltip 
                                        title={plano.totalTarefas !== undefined ? `${plano.tarefasFinalizadas ?? 0}/${plano.totalTarefas} tarefas concluídas` : `Status: ${plano.status || 'N/A'}`}
                                        arrow
                                    >
                                        <Box sx={{ width: '100%' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                                    Progresso
                                                </Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        fontWeight: 'bold',
                                                        color: percentualConclusao === 100 ? 'success.main' : 
                                                               percentualConclusao >= 70 ? 'info.main' : 
                                                               percentualConclusao >= 30 ? 'warning.main' : 'error.main'
                                                    }}
                                                >
                                                    {formatarPercentual(percentualConclusao)}
                                                </Typography>
                                            </Box>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={percentualConclusao} 
                                                color={corProgressBar}
                                                sx={{ 
                                                    height: 6, 
                                                    borderRadius: 3,
                                                    backgroundColor: 'grey.200',
                                                    '& .MuiLinearProgress-bar': {
                                                        borderRadius: 3
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Tooltip>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                                👤
                                            </Typography>
                                            <Typography 
                                                variant="caption" 
                                                color="text.secondary"
                                                sx={{ 
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: '140px'
                                                }}
                                            >
                                                {plano.responsavelNome || '-'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                                📅
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {prazoFormatado}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <Chip 
                                                label={plano.status || 'Sem status'} 
                                                size="small"
                                                color={getStatusColor(plano.status || '')}
                                                sx={{ fontSize: '0.7rem', height: '22px' }}
                                            />
                                            {plano.prioridade && (
                                                <Chip 
                                                    label={`${plano.prioridade}`} 
                                                    size="small"
                                                    color={getPrioridadeColor(plano.prioridade)}
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.7rem', height: '22px' }}
                                                />
                                            )}
                                        </Box>
                                        {plano.area && (
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    color: 'text.secondary',
                                                    fontStyle: 'italic',
                                                    fontSize: '0.7rem'
                                                }}
                                            >
                                                {plano.area}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>

            {/* Modal de Detalhes */}
            {planoSelecionado && (
                <PlanoDeAcaoDetalhesModal
                    open={modalDetalhesAberto}
                    onClose={handleFecharModal}
                    plano={planoSelecionado}
                    onEdit={() => {
                        handleFecharModal();
                        if (planoSelecionado.guid) {
                            onEditar(planoSelecionado.guid);
                        }
                    }}
                />
            )}
        </>
    );
}
